// page-actions/meeting-modes.ts — Chargements asynchrones des modes réunion
// de #actions : rattachement (`?meeting=<id>`) et consultation
// (`?meeting_view=<id>`).
//
// Septième tranche extraite de `renderActionsPage` (point 16, 2026-09-08).
// Les deux modes pré-cochent les actions déjà liées à la réunion. Le
// rattachement fournit en plus le contexte de la barre « Mettre à jour la
// liste » (libellé de la réunion, ids initiaux pour n'animer que les ajouts).
// La page décide quoi redessiner après.
//
// Globaux ambiants : api, t, toast (core-globals.d.ts).

import type { BulkBarLink } from './bulk-bar.js';

interface LinkedActions {
  action_ids: number[];
}

/**
 * Mode rattachement : coche les actions liées dans `selectedIds` et renvoie
 * le contexte de la barre de rattachement. `null` si le chargement échoue,
 * après un toast : la page garde alors sa barre provisoire sans libellé.
 */
export async function loadLinkMode(
  meetingId: number,
  selectedIds: Set<number>,
): Promise<BulkBarLink | null> {
  try {
    const [meetings, linked] = await Promise.all([
      api.getMeetingsRecords() as Promise<Array<{ id: number; label: string }>>,
      api.getMeetingActions(meetingId) as Promise<LinkedActions>,
    ]);
    for (const id of linked.action_ids) selectedIds.add(id);
    return {
      meetingId,
      status: 'ready',
      label: meetings.find((m) => m.id === meetingId)?.label || '',
      initialLinkedIds: new Set(linked.action_ids),
    };
  } catch (err: unknown) {
    toast(err instanceof Error ? err.message : t('common.error'), 'error');
    return null;
  }
}

/**
 * Mode consultation : coche les actions liées, sans barre de rattachement.
 * Renvoie `true` si la sélection a été chargée.
 */
export async function loadViewMode(meetingId: number, selectedIds: Set<number>): Promise<boolean> {
  try {
    const linked = (await api.getMeetingActions(meetingId)) as LinkedActions;
    for (const id of linked.action_ids) selectedIds.add(id);
    return true;
  } catch (err: unknown) {
    toast(err instanceof Error ? err.message : t('common.error'), 'error');
    return false;
  }
}
